import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import Layout from '@/components/Layout';
import CardTopUp from '@/components/CardTopUp';
import {
  declareSettlement, getBillingProfile, getStatements, getTerms, getWallet,
  respondToStatement, respondToTerms, updateBillingProfile,
  type BillingProfile, type CentreStatements, type ProposedTerms,
  type SettlementMethod, type Wallet,
} from '@/lib/api';

const field = 'rounded-lg border border-line-strong px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none';
const METHODS: SettlementMethod[] = ['BankTransfer', 'Cash', 'Card', 'Offset', 'Other'];
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Everything about money, from the centre's side.
 *
 * Three things that are easy to conflate live here side by side, in the order they matter to the
 * person reading:
 *
 *  1. What this centre owes each CLINIC in referral shares — the big number, between two businesses.
 *  2. Terms awaiting its acceptance, because nothing accrues until it agrees.
 *  3. Its wallet with the PLATFORM, which is a different debt entirely and is charged per delivered
 *     view. That ledger has existed for a while with no screen at all, so a centre was being debited
 *     with no way to see the arithmetic.
 */
const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const [statements, setStatements] = useState<CentreStatements | null>(null);
  const [terms, setTerms] = useState<ProposedTerms[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [profile, setProfile] = useState<BillingProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toppingUp, setToppingUp] = useState(false);

  // Declare-a-payment form, per clinic
  const [payFor, setPayFor] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(today());
  const [method, setMethod] = useState<SettlementMethod>('BankTransfer');
  const [reference, setReference] = useState('');

  const money = (v: number, currency: string) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(v); }
    catch { return `${v.toFixed(2)} ${currency}`; }
  };

  const load = useCallback(() => {
    setError(null);
    Promise.all([getStatements(), getTerms(), getWallet(), getBillingProfile()])
      .then(([s, tm, w, p]) => { setStatements(s); setTerms(tm); setWallet(w); setProfile(p); })
      .catch(() => setError(t('billing.loadError', 'Could not load your billing information.')));
  }, [t]);

  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); load(); }
    catch (e: any) {
      setError(e?.response?.data?.title ?? e?.response?.data?.detail
        ?? t('billing.actionError', 'That did not work.'));
    }
    finally { setBusy(false); }
  };

  const declare = (tenantId: string) => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return;
    return act(async () => {
      await declareSettlement(tenantId, {
        amount: value, paidOn, method, reference: reference.trim() || null,
      });
      setAmount(''); setReference(''); setPayFor(null);
    });
  };

  const pendingTerms = terms.filter((x) => !x.acceptedByCentreAt);

  const describeTerms = (x: ProposedTerms) =>
    x.basis === 'None' ? t('billing.noShare', 'No referral share')
      : x.basis === 'PerStudy' ? `${x.amountPerStudy} ${x.currency} ${t('billing.perView', 'per view')}`
        : x.basis === 'PercentOfAgreedTariff' ? `${x.percentOfTariff}% ${t('billing.ofTariff', 'of the agreed tariff')}`
          : x.rates.map((r) => `${r.modality ?? t('billing.anyOther', 'any other')}: ${r.amount}`).join(' · ');

  if (statements === null) {
    return <Layout><div className="flex justify-center py-10"><Spinner size="lg" /></div></Layout>;
  }

  return (
    <Layout>
      <h1 className="mb-4 text-2xl font-bold text-body">{t('billing.title', 'Billing')}</h1>

      {error && <Alert variant="error" className="mb-3">{error}</Alert>}

      {/* Billing details, first if incomplete — a clinic cannot agree paid terms with us until
          these are filled in, and the message they get says to ask us. */}
      {profile && !profile.isComplete && (
        <Card className="mb-4 border-amber-200 bg-amber-50">
          <h2 className="mb-1 text-sm font-semibold text-amber-900">
            {t('billing.completeProfile', 'Complete your billing details')}
          </h2>
          <p className="mb-3 text-xs text-amber-800">
            {t('billing.completeProfileHint',
              'Clinics cannot agree paid referral terms with you until you have a legal name and tax id '
              + 'on file — a statement has to be able to name you.')}
          </p>
          <BillingProfileForm profile={profile} busy={busy} onSave={(p) => act(() => updateBillingProfile(p))} />
        </Card>
      )}

      {/* Terms awaiting a decision */}
      {pendingTerms.length > 0 && (
        <Card className="mb-4">
          <h2 className="mb-1 text-sm font-semibold text-body">
            {t('billing.termsTitle', 'Terms awaiting your acceptance')}
          </h2>
          <p className="mb-3 text-xs text-muted">
            {t('billing.termsHint', 'Nothing is charged to you under these until you accept them.')}
          </p>
          <ul className="divide-y divide-line-soft">
            {pendingTerms.map((x) => (
              <li key={x.id} className="flex flex-wrap items-start justify-between gap-2 py-3">
                <div>
                  <div className="text-sm font-medium text-body">{x.clinicName}</div>
                  <div className="text-sm text-body-soft">{describeTerms(x)}</div>
                  <div className="text-xs text-muted">
                    {t('billing.from', 'From')} {x.effectiveFrom}
                    {x.description && ` · ${x.description}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" loading={busy} onClick={() => act(() => respondToTerms(x.id, true))}>
                    {t('billing.accept', 'Accept')}
                  </Button>
                  <Button
                    variant="ghost" size="sm" loading={busy}
                    onClick={() => {
                      const why = window.prompt(t('billing.declinePrompt', 'Why not?') ?? '');
                      if (why?.trim()) act(() => respondToTerms(x.id, false, why.trim()));
                    }}
                  >
                    {t('billing.decline', 'Decline')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* What we owe each clinic */}
      <Card className="mb-4">
        <h2 className="mb-1 text-sm font-semibold text-body">
          {t('billing.owedTitle', 'What you owe each clinic')}
        </h2>
        <p className="mb-3 text-xs text-muted">
          {t('billing.owedHint',
            'Referral shares, less what you have already paid. A payment settles the whole account, '
            + 'so paying two months at once or paying ahead both work.')}
        </p>

        {statements.accounts.length === 0 ? (
          <p className="text-sm text-muted">{t('billing.owedNone', 'Nothing outstanding.')}</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {statements.accounts.map((a) => (
              <li key={a.tenantId} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium text-body">{a.clinicName}</div>
                    {a.outstanding >= 0 ? (
                      <div className="text-lg font-bold text-body">
                        {money(a.outstanding, a.currency)}
                        <span className="ml-2 text-xs font-normal text-muted">
                          {t('billing.toPay', 'to pay')}
                        </span>
                      </div>
                    ) : (
                      <div className="text-lg font-bold text-brand-ink-strong">
                        {money(-a.outstanding, a.currency)}
                        <span className="ml-2 text-xs font-normal text-muted">
                          {t('billing.paidAhead', 'paid ahead — credit against future referrals')}
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="secondary" size="sm"
                    onClick={() => setPayFor(payFor === a.tenantId ? null : a.tenantId)}
                  >
                    {t('billing.declarePayment', 'I have paid this')}
                  </Button>
                </div>

                {payFor === a.tenantId && (
                  <div className="mt-3 rounded-lg bg-canvas p-3">
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="flex flex-col text-xs text-muted">
                        {t('billing.amount', 'Amount')}
                        <input
                          type="number" min="0" step="0.01" value={amount}
                          onChange={(e) => setAmount(e.target.value)} className={`${field} w-28`}
                        />
                      </label>
                      <label className="flex flex-col text-xs text-muted">
                        {t('billing.paidOn', 'Paid on')}
                        <input
                          type="date" value={paidOn} max={today()}
                          onChange={(e) => setPaidOn(e.target.value)} className={field}
                        />
                      </label>
                      <label className="flex flex-col text-xs text-muted">
                        {t('billing.method', 'Method')}
                        <select
                          value={method} onChange={(e) => setMethod(e.target.value as SettlementMethod)}
                          className={field}
                        >
                          {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </label>
                      <label className="flex flex-col text-xs text-muted">
                        {t('billing.reference', 'Reference')}
                        <input
                          value={reference} onChange={(e) => setReference(e.target.value)}
                          placeholder="OP-8891" className={`${field} w-36`}
                        />
                      </label>
                      <Button loading={busy} disabled={!amount} onClick={() => declare(a.tenantId)}>
                        {t('billing.tellThem', 'Tell them')}
                      </Button>
                    </div>
                    <p className="mt-2 text-xs text-subtle-ink">
                      {t('billing.declareHint',
                        'The clinic confirms it arrived before the balance changes — so a reference they '
                        + 'can find on their bank statement is worth including.')}
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Statements received */}
      <Card className="mb-4">
        <h2 className="mb-3 text-sm font-semibold text-body">
          {t('billing.statementsTitle', 'Statements')}
        </h2>

        {statements.statements.length === 0 ? (
          <p className="text-sm text-muted">{t('billing.statementsNone', 'No statements yet.')}</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {statements.statements.map((s) => (
              <li key={s.id} className="py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium text-body">
                      {s.clinicName}
                      {s.number && (
                        <span className="ml-2 text-xs text-subtle-ink">
                          {s.series}-{String(s.number).padStart(4, '0')}
                        </span>
                      )}
                      <span className={`ml-2 rounded px-1.5 py-0.5 text-[11px] ${
                        s.status === 'Disputed' ? 'bg-red-100 text-red-700'
                          : s.status === 'Acknowledged' ? 'bg-brand-100 text-brand-ink-deep'
                          : 'bg-subtle text-muted-strong'
                      }`}>
                        {s.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted">{s.periodStart} → {s.periodEnd}</div>
                    {/* Written out because this is the arithmetic worth checking before agreeing. */}
                    <div className="mt-1 text-xs text-muted-strong">
                      {t('billing.broughtForward', 'Brought forward')} {money(s.openingBalance, s.currency)}
                      {' + '}{money(s.charges, s.currency)}
                      {' − '}{money(s.settlements, s.currency)}
                      {' = '}
                      <span className="font-semibold text-body">{money(s.closingBalance, s.currency)}</span>
                    </div>
                    {s.disputeReason && (
                      <div className="mt-1 text-xs text-red-700">{s.disputeReason}</div>
                    )}
                  </div>
                  {s.status === 'Issued' && (
                    <div className="flex gap-2">
                      <Button size="sm" loading={busy} onClick={() => act(() => respondToStatement(s.id, true))}>
                        {t('billing.agree', 'These look right')}
                      </Button>
                      <Button
                        variant="ghost" size="sm" loading={busy}
                        onClick={() => {
                          const why = window.prompt(
                            t('billing.disputePrompt', 'What is wrong with it?') ?? '');
                          if (why?.trim()) act(() => respondToStatement(s.id, false, why.trim()));
                        }}
                      >
                        {t('billing.dispute', 'Query')}
                      </Button>
                    </div>
                  )}
                </div>

                {s.lines.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted">
                      {t('billing.showLines', 'Show the lines')}
                    </summary>
                    <table className="mt-2 w-full text-xs">
                      <tbody className="divide-y divide-line-faint">
                        {s.lines.map((l, i) => (
                          <tr key={i}>
                            <td className="py-1 pr-3 whitespace-nowrap text-muted">{l.occurredOn}</td>
                            <td className="py-1 pr-3 text-body-soft">
                              {l.description}
                              {l.isLatePosting && (
                                <span className="ml-1 text-subtle-ink">
                                  ({t('billing.postedLate', 'posted late')})
                                </span>
                              )}
                            </td>
                            <td className="py-1 text-right tabular-nums text-body-strong">
                              {money(l.amount, s.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* The platform wallet — a different debt entirely */}
      {wallet && (
        <Card>
          <h2 className="mb-1 text-sm font-semibold text-body">
            {t('billing.walletTitle', 'Your easyDent wallet')}
          </h2>
          <p className="mb-3 text-xs text-muted">
            {t('billing.walletHint',
              'What you pay easyDent for delivering studies. Separate from what you owe the clinics above.')}
          </p>
          {/* A negative balance means studies were delivered on credit — worth saying, since the
              alternative is a number nobody explains until an invoice arrives. */}
          {wallet.balance < 0 && (
            <Alert variant="warning" className="mb-3">
              {t('billing.walletOverdrawn',
                'You have used more than you have paid in. The difference appears on your next invoice — top up to keep ahead of it.')}
            </Alert>
          )}
          <div className="mb-3 flex flex-wrap gap-6 text-sm">
            <div>
              <div className="text-xs text-muted">{t('billing.balance', 'Balance')}</div>
              <div className={`text-lg font-bold ${wallet.balance < 0 ? 'text-red-700' : 'text-body'}`}>
                {money(wallet.balance, wallet.currency)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted">{t('billing.perView', 'Per view')}</div>
              <div className="text-lg font-bold text-body">
                {wallet.studyFee > 0
                  ? money(wallet.studyFee, wallet.currency)
                  : t('billing.free', 'Free')}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted">{t('billing.thisMonth', 'Charged this month')}</div>
              <div className="text-lg font-bold text-body">
                {money(wallet.chargedThisMonth, wallet.currency)}
              </div>
            </div>
          </div>

          <div className="mb-3">
            <Button size="sm" onClick={() => setToppingUp(true)}>
              {t('billing.topUp', 'Top up by card')}
            </Button>
          </div>

          {wallet.recent.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs text-muted">
                {t('billing.showLedger', 'Show the ledger')}
              </summary>
              <table className="mt-2 w-full text-xs">
                <tbody className="divide-y divide-line-faint">
                  {wallet.recent.map((e, i) => (
                    <tr key={i}>
                      <td className="py-1 pr-3 whitespace-nowrap text-muted">{e.at.slice(0, 10)}</td>
                      <td className="py-1 pr-3 text-body-soft">{e.description ?? e.type}</td>
                      <td className="py-1 pr-3 text-right tabular-nums text-body-strong">
                        {money(e.amount, wallet.currency)}
                      </td>
                      <td className="py-1 text-right tabular-nums text-muted">
                        {money(e.balanceAfter, wallet.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </Card>
      )}

      {toppingUp && wallet && (
        <CardTopUp
          currency={wallet.currency}
          onClose={() => setToppingUp(false)}
          onPaid={() => { setToppingUp(false); void load(); }}
        />
      )}
    </Layout>
  );
};

/** Split out so it can also be reached once complete, from the statements screen. */
const BillingProfileForm: React.FC<{
  profile: BillingProfile;
  busy: boolean;
  onSave: (p: Omit<BillingProfile, 'name' | 'isComplete' | 'missingFields'>) => void;
}> = ({ profile, busy, onSave }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    legalName: profile.legalName ?? '',
    taxId: profile.taxId ?? '',
    registrationNumber: profile.registrationNumber ?? '',
    iban: profile.iban ?? '',
    billingAddress: profile.billingAddress ?? '',
    billingCity: profile.billingCity ?? '',
    billingCountySubdivision: profile.billingCountySubdivision ?? '',
    isVatRegistered: profile.isVatRegistered,
    buyerReference: profile.buyerReference ?? '',
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex flex-col text-xs text-muted-strong">
          {t('billing.legalName', 'Registered company name')}
          <input value={form.legalName} onChange={set('legalName')} className={field} />
        </label>
        <label className="flex flex-col text-xs text-muted-strong">
          {t('billing.taxId', 'Tax id (CUI)')}
          <input value={form.taxId} onChange={set('taxId')} className={field} />
        </label>
        <label className="flex flex-col text-xs text-muted-strong">
          {t('billing.regNumber', 'Trade register number')}
          <input value={form.registrationNumber} onChange={set('registrationNumber')} className={field} />
        </label>
        <label className="flex flex-col text-xs text-muted-strong">
          {t('billing.iban', 'IBAN')}
          <input value={form.iban} onChange={set('iban')} className={field} />
        </label>
        <label className="flex flex-col text-xs text-muted-strong sm:col-span-2">
          {t('billing.billingAddress', 'Registered address')}
          <input value={form.billingAddress} onChange={set('billingAddress')} className={field} />
        </label>
        <label className="flex flex-col text-xs text-muted-strong">
          {t('billing.billingCity', 'Registered city')}
          <input value={form.billingCity} onChange={set('billingCity')} className={field} />
        </label>
        <label className="flex flex-col text-xs text-muted-strong">
          {t('billing.billingCounty', 'County (ISO 3166-2)')}
          <input
            value={form.billingCountySubdivision} onChange={set('billingCountySubdivision')}
            placeholder="RO-CJ" className={field}
          />
        </label>
        {/* BT-10 — theirs to give. Quoted back on our invoices so their accounts-payable system
            can route it; a reference we invented would fail to route and misstate a filed
            document at the same time. */}
        <label className="flex flex-col text-xs text-muted-strong sm:col-span-2">
          {t('billing.buyerReference', 'Your purchase-order reference')}
          <input value={form.buyerReference} onChange={set('buyerReference')} className={field} />
        </label>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-body-soft">
        <input type="checkbox" checked={form.isVatRegistered} onChange={set('isVatRegistered')} />
        {t('billing.vatRegistered', 'Registered for VAT')}
      </label>
      {/* The registered city is asked for separately from the location one because the entity's own
          comment says the registered address need not be where patients are sent. */}
      <p className="mt-2 text-xs text-subtle-ink">
        {t('billing.countyHint',
          'The county is the ISO code (RO-CJ, RO-B …). It goes on the invoice, so it is typed rather '
          + 'than guessed from the city.')}
      </p>
      <Button className="mt-3" loading={busy} onClick={() => onSave(form)}>
        {t('billing.save', 'Save')}
      </Button>
    </div>
  );
};

export default BillingPage;
